'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useEvent, useUnit } from 'effector-react';
import { loadCss, loadJs } from '@nx-frontend/features/web-editor/util';
import { $ModalStore, closeModalEvent, openModalEvent } from '@nx-frontend/shared/ui/modal';
import { Spinner } from '@nx-frontend/shared/ui/spinner';
import { useToast } from '@nx-frontend/shared/ui/toast';
import { setShareSettingsOpened, updateOrg, USER_VARIABLE } from '@nx-frontend/shared/api';
import { generalEnv } from '@nx-frontend/shared/env';
import { $app } from '@nx-frontend/shared/store/app';
import { $billingInfo } from '@nx-frontend/shared/store/billing-info/billing-info';
import { $isFreePlan } from '@nx-frontend/shared/store/billing-info/billing-info';
import { $currentOrganization } from '@nx-frontend/shared/store/current-organization/current-organization';
import { setCurrentOrganizationEvent } from '@nx-frontend/shared/store/current-organization/current-organization';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import { $orgCoupons } from '@nx-frontend/shared/store/org-coupons';
import {
  $orgInfo,
  $canSetPublicPagePassword,
  $isCanManageDomain,
  setOrgInfoEvent,
} from '@nx-frontend/shared/store/org-info';
import { $orgRoleState } from '@nx-frontend/shared/store/org-role';
import { $user } from '@nx-frontend/shared/store/user';
import {
  $isShareSettingsOpened,
  $isShareAnalyticsShown,
  setUserVarsEvent,
  $userVars,
} from '@nx-frontend/shared/store/user-vars';
import {
  OrgMemberRole,
  OrgInfoSubscriptionType,
  PaywallPopupPayload,
  PaywallPopupReason,
  PopupType,
  SharePopupPayload,
} from '@nx-frontend/shared/types';
import { GetOpenSharePopupProps } from '@nx-frontend/shared/types';
import { copyToClipboard } from '@nx-frontend/shared/utils';
import { getSubscriptionType, mapSubscriptionType } from '@nx-frontend/shared/utils';
import { getAdminClientDirectUrl, getGuideClientUrl, getPricingClientUrl } from '@nx-frontend/shared/utils';
import { getOpenPaywallPopup } from './paywall-popup';

const webComponentTag = 'nim-share-form';
const isSharePopupBundleLoaded = () => document.createElement(webComponentTag).constructor !== HTMLElement;

enum CUSTOMIZE_POPUP_CLOSE_EVENT {
  GO_TO_SETTINGS = 'goToSettings',
  LEARN_MORE_NO_CNAME = 'learnMoreNoCname',
}

export const logLevels = {
  error: 0,
  warn: 1,
  log: 2,
  debug: 3,
} as const;

export type LogLevelType = keyof typeof logLevels;
export const DEFAULT_LEVEL: LogLevelType = 'error';

export const getOpenSharePopup = ({ openModal, payload }: GetOpenSharePopupProps) => {
  openModal({
    content: PopupType.SHARE,
    showCloseButton: false,
    usePadding: false,
    payload,
  });
};

export function SharePopup() {
  const [
    modalStore,
    app,
    user,
    userVars,
    orgInfo,
    billingInfo,
    currentWorkspace,
    currentOrganization,
    role,
    canSetPublicPagePassword,
    isShareSettingsOpened,
    isCanManageDomain,
    isShareAnalyticsShown,
    isFreePlan,
    orgCoupons,
  ] = useUnit([
    $ModalStore,
    $app,
    $user,
    $userVars,
    $orgInfo,
    $billingInfo,
    $currentWorkspace,
    $currentOrganization,
    $orgRoleState,
    $canSetPublicPagePassword,
    $isShareSettingsOpened,
    $isCanManageDomain,
    $isShareAnalyticsShown,
    $isFreePlan,
    $orgCoupons,
  ]);

  const { toast } = useToast();
  const t = useTranslations('webnotes');
  const [closeModal, openModal] = useUnit([closeModalEvent, openModalEvent]);
  const [isLoaded, setIsLoaded] = useState(false);
  const nimShareRef = useRef<HTMLDivElement>(null);
  const language = 'en';

  let workspaceId = currentWorkspace ? currentWorkspace.globalId : '';
  let globalId = '';

  const modal = modalStore.find((modal) => modal.payload && modal.payload.payloadType === PopupType.SHARE);
  const payload = modal?.payload as SharePopupPayload;

  if (payload) {
    if (payload.workspaceId) {
      workspaceId = payload.workspaceId;
    }
    if (payload.globalId) {
      globalId = payload.globalId;
    }
  }

  const subscriptionType = getSubscriptionType({ orgInfo, orgCoupons, billingInfo });
  const nimSubscriptionType = mapSubscriptionType(subscriptionType) as OrgInfoSubscriptionType;

  const canUseShareAd = [OrgInfoSubscriptionType.STANDARD, OrgInfoSubscriptionType.PLUS].includes(nimSubscriptionType);

  const isShareAdEnabled = currentOrganization ? currentOrganization.isShareAdEnabled : false;
  const currentOrgRole = role && role.role ? role.role : OrgMemberRole.GUEST;
  const nimIsOwner = currentOrgRole === OrgMemberRole.OWNER || currentOrgRole === OrgMemberRole.MANAGER;
  const nimShowBanner = currentOrgRole === OrgMemberRole.OWNER;
  const nimEnablePremiumFeature = canSetPublicPagePassword;
  const nimIsSettingsOpened = !!isShareSettingsOpened;
  const nimIsCustomizeEnabled = isCanManageDomain || (!isCanManageDomain && nimIsOwner);
  const nimShowDisableShareAd = nimIsOwner && canUseShareAd;
  const nimEnableAnalytics = nimIsOwner;
  const nimChecklistAnalyticsShown = !!isShareAnalyticsShown;
  const nimDisableShareAdChecked = isShareAdEnabled && canUseShareAd;
  const nimIsBusinessOrg = [
    OrgInfoSubscriptionType.BUSINESS,
    OrgInfoSubscriptionType.BUSINESS2023,
    OrgInfoSubscriptionType.BUSINESS2024,
  ].includes(nimSubscriptionType);
  const nimIsHasCname = !!(currentOrganization && currentOrganization.domain);
  const nimIsBusinessTrialExpired = billingInfo ? billingInfo.plan === 'trial' && !billingInfo.active : false;
  const nimDiamond = null;

  const setOrgInfo = useEvent(setOrgInfoEvent);
  const setCurrentOrganization = useEvent(setCurrentOrganizationEvent);
  const setUserVars = useEvent(setUserVarsEvent);

  const onSharePopupLoaded = useCallback(() => {
    if (document.querySelectorAll(webComponentTag).length === 0) {
      const nimShare = document.createElement(webComponentTag);
      const nimSharePayload = {
        apiGatewayUrl: generalEnv.gatewayUrl,
        apiGatewayToken: app.sessionId,
        nimLanguage: language,
        logLevel: DEFAULT_LEVEL,
        nimDiamond,
        nimEnablePremiumFeature,
        nimIsSettingsOpened,
        nimEnableAnalytics,
        nimChecklistAnalyticsShown,
        nimShowDisableShareAd,
        nimDisableShareAdChecked,
        nimIsCustomizeEnabled,
        nimIsBusinessOrg,
        nimIsHasCname,
        nimIsOwner,
        nimShowBanner,
        nimIsBusinessTrialExpired,
        featureContext: {
          workspaceId,
          noteId: globalId,
        },
      };
      Object.assign(nimShare, nimSharePayload);

      nimShare.addEventListener('nimOnCustomizePopupClose', (event) => {
        const { detail } = event as { detail?: CUSTOMIZE_POPUP_CLOSE_EVENT };
        let url = getPricingClientUrl(currentOrganization.globalId);
        if (detail === CUSTOMIZE_POPUP_CLOSE_EVENT.GO_TO_SETTINGS) {
          url = getAdminClientDirectUrl(generalEnv.adminClientUrl, currentOrganization.globalId);
        } else if (detail === CUSTOMIZE_POPUP_CLOSE_EVENT.LEARN_MORE_NO_CNAME) {
          url = getGuideClientUrl('branding/custom-subdomains-cname');
        }
        window.open(url, '_blank');
      });

      nimShare.addEventListener('nimSettingsToggle', (event) => {
        if (!userVars) {
          return;
        }
        const { detail } = event as { detail?: { isOpened: boolean } };
        const value = detail?.isOpened || false;

        const payload = {
          userId: user?.id,
          value,
        };
        setShareSettingsOpened(payload).then(() => {
          let updatedUserVars = userVars ? [...userVars] : [];

          const propExists = updatedUserVars.find((userVar) => userVar.key === USER_VARIABLE.SHARE_SETTINGS_OPENED);
          if (propExists) {
            updatedUserVars = updatedUserVars.map((userVar) => {
              if (userVar.key === USER_VARIABLE.SHARE_SETTINGS_OPENED) {
                userVar.value = value;
              }
              return userVar;
            });
          } else {
            updatedUserVars.push({
              key: USER_VARIABLE.SHARE_SETTINGS_OPENED,
              value,
            });
          }

          setUserVars([...updatedUserVars]);
        });
      });

      nimShare.addEventListener('nimDisableShareAdChange', (event) => {
        if (!orgInfo) {
          return;
        }
        const { detail } = event as { detail?: boolean };
        const isShareAdEnabled = !!detail;

        updateOrg({
          userId: user?.id,
          orgId: currentOrganization.globalId,
          payload: {
            isShareAdEnabled,
          },
        }).then(() => {
          setOrgInfo({
            ...orgInfo,
            isShareAdEnabled,
          });
          setCurrentOrganization({
            ...currentOrganization,
            isShareAdEnabled,
          });
        });
      });

      nimShare.addEventListener('nimCloseClick', () => {
        closeModal();
      });

      nimShare.addEventListener('nimPremiumFeatureClick', () => {
        if (isFreePlan) {
          closeModal();

          const paywallPopupPayload: PaywallPopupPayload = {
            payloadType: PopupType.PAYWALL,
            title: `Don't settle for less: become limitless after upgrade!`,
            reason: PaywallPopupReason.SET_PAGE_PASSWORD,
          };

          getOpenPaywallPopup({ openModal, payload: paywallPopupPayload });
        }
      });

      nimShare.addEventListener('nimSubmitClick', (event) => {
        console.log(event);
        const { detail } = event as { detail?: { shareDetails: string } };
        if (detail && detail.shareDetails) {
          copyToClipboard(detail.shareDetails);
          toast({ title: t('webnotes-public_link_copied_toast') });
        }
        closeModal();
      });

      nimShareRef.current?.appendChild(nimShare);
    }
    setIsLoaded(true);
  }, [
    app.sessionId,
    nimEnablePremiumFeature,
    nimIsSettingsOpened,
    nimEnableAnalytics,
    nimChecklistAnalyticsShown,
    nimShowDisableShareAd,
    nimDisableShareAdChecked,
    nimIsCustomizeEnabled,
    nimIsBusinessOrg,
    nimIsHasCname,
    nimIsOwner,
    nimShowBanner,
    nimIsBusinessTrialExpired,
    workspaceId,
    globalId,
    currentOrganization,
    userVars,
    user?.id,
    setUserVars,
    orgInfo,
    setOrgInfo,
    setCurrentOrganization,
    closeModal,
    isFreePlan,
    openModal,
    toast,
    t,
  ]);

  useEffect(() => {
    loadCss({ url: generalEnv.tasksBundleCssUrl, id: 'tasks-bundle-css' });
    loadJs({ url: generalEnv.shareBundleJsUrl, id: 'share-popup-bundle' });

    let IID: NodeJS.Timer | number | null = null;
    if (!isSharePopupBundleLoaded()) {
      IID = setInterval(() => {
        if (isSharePopupBundleLoaded()) {
          onSharePopupLoaded();
          IID && clearInterval(IID);
        }
      }, 1000);
    } else {
      onSharePopupLoaded();
    }

    return () => {
      IID && clearInterval(IID);
    };
  }, [onSharePopupLoaded]);

  if (!globalId) {
    return null;
  }

  return (
    <div
      data-cy="share-popup"
      className="min-w-[520px] py-0 min-h-[360px] relative nimbus-popup-content"
      ref={nimShareRef}
    >
      {!isLoaded && (
        <div className="flex w-full h-full absolute items-center justify-center">
          <Spinner />
        </div>
      )}
    </div>
  );
}
